import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import Reveal from '../components/Reveal';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import './Properties.css';

const PropertyDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Touch state for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px) 
  const minSwipeDistance = 50; 

  const onTouchStart = (e) => {
    setTouchEnd(null); // Reset
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNextImage();
    }
    if (isRightSwipe) {
      handlePrevImage();
    }
  };

  const handleNextImage = () => {
    if (!property || !property.images) return;
    setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
  };

  const handlePrevImage = () => {
    if (!property || !property.images) return;
    setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
  };

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const docRef = doc(db, "properties", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          // Ensure images array exists
          if (!data.images && data.image) {
            data.images = [data.image];
          }
          setProperty({ id: docSnap.id, ...data });
          setCurrentImageIndex(0);
        } else {
          setError("Property not found");
        }
      } catch (err) {
        console.error("Error fetching property:", err);
        setError("Error fetching details");
      }
      setLoading(false);
    };

    fetchProperty();
  }, [id]);

  if (loading) return <div className="container py-5 text-center"><h3>Loading details...</h3></div>;
  
  if (error || !property) {
    return (
      <div className="container" style={{padding: '100px 20px', textAlign: 'center'}}>
        <h2>Property Not Found</h2>
        <Link to="/properties" className="btn btn-primary">Back to Properties</Link>
      </div>
    );
  }

  return (
    <div className="property-details-page">
      <div className="container">
        <Reveal>
          <Link to="/properties" style={{display: 'inline-flex', alignItems: 'center', marginBottom: '20px', color: '#666', textDecoration: 'none'}}>
            <ArrowLeft size={20} style={{marginRight: '8px'}} /> Back to Properties
          </Link>
          
          <div className="property-details-content">
            <div className="property-details-header" style={{marginBottom: '30px'}}>
               <h1 style={{fontSize: '2.5rem', marginBottom: '10px'}}>{property.title}</h1>
               <div style={{display: 'flex', alignItems: 'center', color: '#666'}}>
                  <MapPin size={20} style={{marginRight: '8px'}} />
                  {property.location}
               </div>
            </div>

            <div 
              className="property-image-large" 
              style={{marginBottom: '20px', borderRadius: '12px', overflow: 'hidden', position: 'relative'}}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
               <img 
                 src={property.images ? property.images[currentImageIndex] : property.image} 
                 alt={property.title} 
                 style={{width: '100%', height: 'auto', maxHeight: '600px', objectFit: 'cover'}} 
               />
               
               {property.images && property.images.length > 1 && (
                 <>
                   <button 
                     onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                     style={{
                       position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)',
                       background: 'rgba(255, 255, 255, 0.7)', border: 'none', borderRadius: '50%',
                       width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                       cursor: 'pointer', zIndex: 2
                     }}
                   >
                     <ChevronLeft size={24} color="#333" />
                   </button>
                   <button 
                     onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                     style={{
                       position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)',
                       background: 'rgba(255, 255, 255, 0.7)', border: 'none', borderRadius: '50%',
                       width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                       cursor: 'pointer', zIndex: 2
                     }}
                   >
                     <ChevronRight size={24} color="#333" />
                   </button>
                   <div style={{
                     position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)',
                     background: 'rgba(0,0,0,0.5)', padding: '4px 10px', borderRadius: '20px', color: 'white', fontSize: '12px'
                   }}>
                     {currentImageIndex + 1} / {property.images.length}
                   </div>
                 </>
               )}
            </div>

            {/* Image Gallery Thumbnails */}
            {property.images && property.images.length > 1 && (
              <div className="property-thumbnails" style={{display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '20px', marginBottom: '20px'}}>
                {property.images.map((img, index) => (
                  <div 
                    key={index} 
                    onClick={() => setCurrentImageIndex(index)}
                    style={{
                      width: '100px', 
                      height: '80px', 
                      borderRadius: '8px', 
                      overflow: 'hidden', 
                      cursor: 'pointer',
                      border: currentImageIndex === index ? '2px solid var(--color-primary)' : '2px solid transparent',
                      opacity: currentImageIndex === index ? 1 : 0.7,
                      transition: 'all 0.2s',
                      flexShrink: 0
                    }}
                  >
                    <img src={img} alt={`${property.title} view ${index + 1}`} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  </div>
                ))}
              </div>
            )}

            <div className="property-info-grid property-details-grid">
               <div className="left-column">
                  <div className="features-bar">
                     <div className="feature">
                        <Bed size={24} /> <span>{property.beds} Beds</span>
                     </div>
                     <div className="feature">
                        <Bath size={24} /> <span>{property.baths} Baths</span>
                     </div>
                     <div className="feature">
                        <Square size={24} /> <span>{property.sqft} sqft</span>
                     </div>
                  </div>

                  <div className="description">
                     <h3 style={{marginBottom: '16px'}}>Description</h3>
                     <p style={{lineHeight: '1.6', color: '#444'}}>{property.description}</p>
                  </div>
               </div>

               <div className="right-column">
                  <div className="inquiry-card">
                     <h3 style={{marginBottom: '20px'}}>Interested?</h3>
                     {property.price && <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', marginBottom: '20px'}}>₦{property.price}</div>}
                     <Link to={`/contact?inquiry=${encodeURIComponent(property.title)}`} className="btn btn-primary" style={{width: '100%', textAlign: 'center', display: 'block'}}>
                        {property.tag === "For Sale" ? "Inquire Now" : "Request Details"}
                     </Link>
                  </div>
               </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default PropertyDetails;
